<script setup>
// Spec 100 — Image Studio: a global full-screen tool. Shared, rights-scoped media
// library (project folders + custom folders + uploads), royalty-free online search,
// and a client-side mini editor (crop / flip / adjust / resize / compress). Mounted
// once at the app root; driven by the imageStudio store; resolves with the chosen
// image and closes in place (no navigation).
import { ref, reactive, computed, watch, nextTick } from 'vue'
import SelectMenu from '../Base/SelectMenu.vue'
import api from '@/lib/axios'
import { useImageStudioStore } from '@/stores/imageStudio'
import {
    Image as ImageIcon, X, Search, Upload, UploadCloud, FolderPlus, Folder, LayoutGrid,
    Clock, Star, ShieldCheck, ChevronLeft, RotateCcw, RotateCw, FlipHorizontal2,
    FlipVertical2, Link2, Check, Loader2, CornerDownRight, Pencil, Trash2, FolderInput,
} from 'lucide-vue-next'

const store = useImageStudioStore()
const open = computed(() => store.isOpen)
const projectId = computed(() => store.options?.projectId ?? null)

const view = ref('library')     // 'library' | 'online' | 'edit'
const loading = ref(false)
const busy = ref(false)
const error = ref('')

// library
const folders = ref({ all_count: 0, projects: [], folders: [], projects_visible: 0, projects_total: 0 })
const active = reactive({ type: 'all', id: null, label: 'All media' })
const assets = ref([])
const libQuery = ref('')

// online
const source = ref('pexels')
const onlineQuery = ref('')
const results = ref([])

// edit
const editSrc = ref('')
const editAlt = ref('')
const editCredit = ref('')
const fromLibrary = ref(false)
const dirty = ref(false)
const imgRef = ref(null)
const natW = ref(0), natH = ref(0), dispW = ref(0), dispH = ref(0), scale = ref(1)
const crop = reactive({ x: 0, y: 0, w: 0, h: 0 })
const aspect = ref('free')
const flipH = ref(false), flipV = ref(false)
const adj = reactive({ bw: false, br: 100, co: 100, sa: 100, se: 0, bl: 0 })
const size = reactive({ ow: 0, oh: 0 })
const sizeLock = ref(true)
let sizeCustom = false
const format = ref('image/webp')
const quality = ref(0.82)
const estKB = ref(null)
let objectUrl = null

const ASPECTS = [['free', 'Free'], [1, '1:1'], [4 / 3, '4:3'], [3 / 2, '3:2'], [16 / 9, '16:9']]
const PRESETS = [['', 'Preset sizes…'], ['1080x1080', 'Square — 1080×1080'], ['1200x630', 'Social / OG — 1200×630'], ['1920x1080', 'Full HD — 1920×1080'], ['1600x900', 'Hero — 1600×900']]

function reset() { view.value = 'library'; error.value = ''; libQuery.value = '' }
function freeObj() { if (objectUrl) { try { URL.revokeObjectURL(objectUrl) } catch (_) {} objectUrl = null } }

watch(open, (v) => {
    if (!v) { freeObj(); return }
    reset()
    active.type = projectId.value ? 'project' : 'all'
    active.id = projectId.value || null
    loadFolders(); loadAssets()
})

// ── Library ───────────────────────────────────────────────────────────────────
async function loadFolders() {
    try { const { data } = await api.get('/media/folders'); folders.value = data.data } catch (e) { /* keep */ }
}
async function loadAssets() {
    loading.value = true; error.value = ''
    try {
        const params = { q: libQuery.value || undefined }
        if (active.type === 'project') params.project = active.id
        else if (active.type === 'folder') params.folder = active.id
        const { data } = await api.get('/media/assets', { params })
        assets.value = data.data.assets || []
    } catch (e) { assets.value = []; error.value = 'Could not load media.' } finally { loading.value = false }
}
function pickFolder(type, id, label) { active.type = type; active.id = id; active.label = label; loadAssets() }

// Inline folder creation (replaces window.prompt): click + → type a name → Enter.
const creatingFolder = ref(false)
const newFolderName = ref('')
const folderInput = ref(null)
function startNewFolder() { creatingFolder.value = true; newFolderName.value = ''; nextTick(() => folderInput.value?.focus()) }
function cancelNewFolder() { creatingFolder.value = false; newFolderName.value = '' }
async function submitNewFolder() {
    const name = newFolderName.value.trim(); if (!name) { cancelNewFolder(); return }
    try {
        const { data } = await api.post('/media/folders', { name })
        creatingFolder.value = false; newFolderName.value = ''
        await loadFolders()
        const f = data.data?.folder
        if (f) pickFolder('folder', f.id, f.name)
    } catch (e) { error.value = 'Could not create folder.' }
}

// Move an image to a project / custom folder / All media (root).
const moveMenuId = ref(null)
function toggleMoveMenu(a) { moveMenuId.value = moveMenuId.value === a.id ? null : a.id }
function closeMoveMenu() { moveMenuId.value = null }
async function moveAsset(a, target_type, target_id = null) {
    closeMoveMenu()
    try {
        await api.patch(`/media/assets/${a.id}/move`, { target_type, target_id })
        // Drop it from the current view if it no longer belongs here.
        const stays = active.type === 'all'
            || (active.type === 'project' && target_type === 'project' && target_id === active.id)
            || (active.type === 'folder' && target_type === 'folder' && target_id === active.id)
        if (!stays) assets.value = assets.value.filter(x => x.id !== a.id)
        loadFolders()
    } catch (e) { error.value = 'Could not move that image.' }
}

// Delete a custom folder (soft, no cascade): its images detach to All media.
const folderToDelete = ref(null)
function askDeleteFolder(f) { folderToDelete.value = f }
function cancelDeleteFolder() { folderToDelete.value = null }
async function confirmDeleteFolder() {
    const f = folderToDelete.value; if (!f) return
    folderToDelete.value = null
    try {
        await api.delete(`/media/folders/${f.id}`)
        if (active.type === 'folder' && active.id === f.id) pickFolder('all', null, 'All media')
        loadFolders()
    } catch (e) { error.value = 'Could not delete that folder.' }
}

// Soft delete (deletion standard): the file/row survive; super-admin Recovery restores.
async function deleteAsset(a) {
    if (!window.confirm(`Delete "${a.filename}"? You can recover it from the super-admin Recovery panel.`)) return
    try {
        await api.delete(`/media/assets/${a.id}`)
        assets.value = assets.value.filter(x => x.id !== a.id)
        loadFolders()
    } catch (e) { error.value = 'Could not delete that image.' }
}
async function onLibUpload(e) {
    const f = e.target.files?.[0]; if (!f) return; e.target.value = ''
    busy.value = true
    try {
        const fd = new FormData(); fd.append('image', f)
        if (active.type === 'project') fd.append('project_id', active.id)
        else if (active.type === 'folder') fd.append('folder_id', active.id)
        else if (projectId.value) fd.append('project_id', projectId.value)
        await api.post('/media/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
        loadAssets(); loadFolders()
    } catch (e) { error.value = 'Upload failed.' } finally { busy.value = false }
}

// ── Online search ──────────────────────────────────────────────────────────────
async function runSearch(reset = true) {
    if (!onlineQuery.value.trim()) return
    loading.value = true; error.value = ''
    try {
        const { data } = await api.get('/media/search', { params: { q: onlineQuery.value, source: source.value, page: 1 } })
        results.value = data.data.photos || []
    } catch (e) {
        results.value = []
        error.value = e?.response?.status === 503 ? 'Pexels not configured — add PEXELS_API_KEY, or use Openverse.' : (e?.response?.data?.message || 'Search failed.')
    } finally { loading.value = false }
}
watch(source, () => { if (onlineQuery.value.trim()) runSearch() })

async function pickStock(r) {
    busy.value = true; error.value = ''
    try {
        const body = { url: r.url, credit: r.credit, source: (r.source || '').toLowerCase() || 'url' }
        if (projectId.value) body.project_id = projectId.value
        const { data } = await api.post('/media/import-url', body)
        openEdit(data.data.asset.url, r.alt || '', r.credit || '', true)
    } catch (e) { error.value = 'Could not import that image.' } finally { busy.value = false }
}

// ── Editor ───────────────────────────────────────────────────────────────────
function openLibraryAsset(a) { openEdit(a.url, a.filename || '', a.credit || '', true) }
function onFreshUpload(e) {
    const f = e.target.files?.[0]; if (!f) return; e.target.value = ''
    freeObj(); objectUrl = URL.createObjectURL(f)
    openEdit(objectUrl, f.name.replace(/\.[^.]+$/, ''), '', false)
}
function openEdit(src, alt, credit, isLibrary) {
    editSrc.value = src; editAlt.value = alt; editCredit.value = credit; fromLibrary.value = isLibrary
    dirty.value = false; flipH.value = false; flipV.value = false; aspect.value = 'free'; sizeCustom = false
    Object.assign(adj, { bw: false, br: 100, co: 100, sa: 100, se: 0, bl: 0 })
    view.value = 'edit'
}
function touch() { dirty.value = true; queueEst() }

function onImgLoad() {
    const im = imgRef.value; if (!im) return
    natW.value = im.naturalWidth || 1; natH.value = im.naturalHeight || 1
    const r = Math.min(700 / natW.value, 440 / natH.value)
    dispW.value = Math.round(natW.value * r); dispH.value = Math.round(natH.value * r)
    scale.value = natW.value / dispW.value
    crop.x = 0; crop.y = 0; crop.w = dispW.value; crop.h = dispH.value
    if (!sizeCustom) { size.ow = natW.value; size.oh = natH.value }
    queueEst()
}
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v))
function setAspect(a) {
    aspect.value = a; if (a === 'free') { touch(); return }
    const ar = Number(a); let w = dispW.value, h = w / ar
    if (h > dispH.value) { h = dispH.value; w = h * ar }
    crop.w = Math.round(w); crop.h = Math.round(h); crop.x = Math.round((dispW.value - w) / 2); crop.y = Math.round((dispH.value - h) / 2)
    syncSizeToCrop(); touch()
}
let drag = null
function pd(e, mode) { e.preventDefault(); drag = { mode, sx: e.clientX, sy: e.clientY, o: { ...crop } }; window.addEventListener('pointermove', pm); window.addEventListener('pointerup', pu) }
function pm(e) {
    if (!drag) return
    const dx = e.clientX - drag.sx, dy = e.clientY - drag.sy; let { x, y, w, h } = drag.o
    if (drag.mode === 'move') { x = clamp(x + dx, 0, dispW.value - w); y = clamp(y + dy, 0, dispH.value - h) }
    else {
        if (drag.mode.includes('e')) w = drag.o.w + dx
        if (drag.mode.includes('s')) h = drag.o.h + dy
        if (drag.mode.includes('w')) { w = drag.o.w - dx; x = drag.o.x + dx }
        if (drag.mode.includes('n')) { h = drag.o.h - dy; y = drag.o.y + dy }
        w = Math.max(24, w); h = Math.max(24, h)
        const ar = aspect.value === 'free' ? null : Number(aspect.value)
        if (ar) { h = w / ar; if (drag.mode.includes('n')) y = drag.o.y + drag.o.h - h }
        if (x < 0) { w += x; x = 0 } if (y < 0) { h += y; y = 0 }
        w = Math.min(w, dispW.value - x); h = Math.min(h, dispH.value - y)
    }
    crop.x = Math.round(x); crop.y = Math.round(y); crop.w = Math.round(w); crop.h = Math.round(h)
    syncSizeToCrop(); touch()
}
function pu() { drag = null; window.removeEventListener('pointermove', pm); window.removeEventListener('pointerup', pu) }
function syncSizeToCrop() { if (!sizeCustom) { size.ow = Math.round(crop.w * scale.value); size.oh = Math.round(crop.h * scale.value) } }

function rotate(deg) {
    const im = imgRef.value; if (!im) return
    const c = document.createElement('canvas'); c.width = im.naturalHeight; c.height = im.naturalWidth
    const x = c.getContext('2d'); x.translate(c.width / 2, c.height / 2); x.rotate(deg * Math.PI / 180)
    x.drawImage(im, -im.naturalWidth / 2, -im.naturalHeight / 2)
    editSrc.value = c.toDataURL('image/png'); sizeCustom = false; dirty.value = true
}
const AR = computed(() => (crop.h ? (crop.w / crop.h) : 1))
function onW() { if (sizeLock.value) size.oh = Math.round(size.ow / AR.value); sizeCustom = true; touch() }
function onH() { if (sizeLock.value) size.ow = Math.round(size.oh * AR.value); sizeCustom = true; touch() }
function applyPreset(v) { if (!v) return; const p = v.split('x'); size.ow = +p[0]; size.oh = +p[1]; sizeCustom = true; touch() }

const filterStyle = computed(() => ({
    filter: `grayscale(${adj.bw ? 1 : 0}) brightness(${adj.br / 100}) contrast(${adj.co / 100}) saturate(${adj.bw ? 1 : adj.sa / 100}) sepia(${adj.se / 100}) blur(${adj.bl}px)`,
    transform: `scaleX(${flipH.value ? -1 : 1}) scaleY(${flipV.value ? -1 : 1})`,
    width: dispW.value + 'px', height: dispH.value + 'px',
}))
function filterStr() { return `grayscale(${adj.bw ? 1 : 0}) brightness(${adj.br / 100}) contrast(${adj.co / 100}) saturate(${adj.bw ? 1 : adj.sa / 100}) sepia(${adj.se / 100}) blur(${adj.bl}px)` }
function buildCanvas() {
    const img = imgRef.value
    const sx = crop.x * scale.value, sy = crop.y * scale.value, sw = crop.w * scale.value, sh = crop.h * scale.value
    const inter = document.createElement('canvas'); inter.width = Math.max(1, Math.round(sw)); inter.height = Math.max(1, Math.round(sh))
    const ic = inter.getContext('2d'); ic.filter = filterStr(); ic.save()
    ic.translate(flipH.value ? inter.width : 0, flipV.value ? inter.height : 0); ic.scale(flipH.value ? -1 : 1, flipV.value ? -1 : 1)
    ic.drawImage(img, sx, sy, sw, sh, 0, 0, inter.width, inter.height); ic.restore()
    const out = document.createElement('canvas'); out.width = Math.max(1, size.ow || inter.width); out.height = Math.max(1, size.oh || inter.height)
    const oc = out.getContext('2d'); oc.imageSmoothingEnabled = true; oc.imageSmoothingQuality = 'high'
    oc.drawImage(inter, 0, 0, inter.width, inter.height, 0, 0, out.width, out.height)
    return out
}
function toBlob() { return new Promise((res) => { try { buildCanvas().toBlob(res, format.value, Number(quality.value)) } catch (e) { res(null) } }) }
let estT = null
function queueEst() { clearTimeout(estT); estT = setTimeout(async () => { const b = await toBlob(); estKB.value = b ? Math.round(b.size / 1024) : null }, 300) }
watch([format, quality], queueEst)

async function encodeAndUpload() {
    const blob = await toBlob(); if (!blob) throw new Error('encode')
    const ext = format.value === 'image/webp' ? 'webp' : (format.value === 'image/png' ? 'png' : 'jpg')
    const fd = new FormData(); fd.append('image', blob, `${(editAlt.value || 'image').replace(/[^a-z0-9-_]+/gi, '-').slice(0, 40)}.${ext}`)
    if (projectId.value) fd.append('project_id', projectId.value)
    else if (active.type === 'folder') fd.append('folder_id', active.id)
    fd.append('source', 'edit')
    const { data } = await api.post('/media/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
    return data.data.asset
}
async function useImage() {
    busy.value = true; error.value = ''
    try {
        let url = editSrc.value
        if (dirty.value || !fromLibrary.value) { const a = await encodeAndUpload(); url = a.url }
        store.apply({ url, alt: (editCredit.value || editAlt.value || '').slice(0, 180) })
    } catch (e) { error.value = 'Could not process the image.' } finally { busy.value = false }
}
async function saveToLibrary() {
    busy.value = true; error.value = ''
    try { await encodeAndUpload(); view.value = 'library'; loadAssets(); loadFolders() }
    catch (e) { error.value = 'Could not save.' } finally { busy.value = false }
}
function close() { store.close() }
function onKey(e) { if (!open.value) return; if (e.key === 'Escape') { if (moveMenuId.value) closeMoveMenu(); else if (view.value === 'edit') view.value = 'library'; else close() } }
watch(open, (v) => { if (v) window.addEventListener('keydown', onKey); else window.removeEventListener('keydown', onKey) })
</script>

<template>
    <teleport to="body">
        <div v-if="open" class="is-overlay">
            <div class="is-modal" data-testid="image-studio">
                <!-- Header -->
                <header class="is-head">
                    <div class="w-8 h-8 rounded-lg bg-indigo-600 text-white grid place-items-center"><ImageIcon class="w-4 h-4" /></div>
                    <div class="leading-tight">
                        <div class="text-[15px] font-bold text-slate-900">Image Studio</div>
                        <div v-if="store.options?.title" class="text-[11px] text-slate-400 flex items-center gap-1"><CornerDownRight class="w-3 h-3" /> {{ store.options.title }}</div>
                    </div>
                    <div class="flex-1"></div>
                    <button class="tabbtn" :class="{ 'seg-on': view === 'library' }" data-testid="is-tab-library" @click="view = 'library'">Library</button>
                    <button class="tabbtn" :class="{ 'seg-on': view === 'online' }" data-testid="is-tab-online" @click="view = 'online'">Search online</button>
                    <div class="w-px h-6 bg-slate-200 mx-1"></div>
                    <button class="is-x" data-testid="is-close" @click="close"><X class="w-5 h-5" /></button>
                </header>

                <div class="flex-1 flex min-h-0">
                    <!-- Sidebar -->
                    <aside v-if="view !== 'edit'" class="w-[248px] border-r border-slate-200 flex flex-col min-h-0 bg-slate-50/60">
                        <div class="p-3 overflow-y-auto flex-1">
                            <div class="nav-h">Library</div>
                            <button class="navrow" :class="{ on: active.type === 'all' }" @click="pickFolder('all', null, 'All media')"><LayoutGrid class="w-4 h-4 text-slate-400" /> All media <span class="ml-auto text-[11px] text-slate-500">{{ folders.all_count }}</span></button>
                            <div class="nav-h mt-4">Projects</div>
                            <button v-for="p in folders.projects" :key="'p' + p.id" class="navrow" :class="{ on: active.type === 'project' && active.id === p.id }" @click="pickFolder('project', p.id, p.name)"><Folder class="w-4 h-4 text-amber-500" /> <span class="truncate">{{ p.name }}</span> <span class="ml-auto text-[11px] text-slate-500">{{ p.count }}</span></button>

                            <div class="nav-h mt-4 flex items-center justify-between"><span>Folders</span><button class="text-slate-400 hover:text-indigo-600" title="New folder" @click="startNewFolder"><FolderPlus class="w-4 h-4" /></button></div>
                            <div v-if="creatingFolder" class="px-2 pb-1">
                                <input ref="folderInput" v-model="newFolderName" @keyup.enter="submitNewFolder" @keyup.esc="cancelNewFolder" @blur="cancelNewFolder" placeholder="Folder name" class="w-full h-8 px-2.5 rounded-lg bg-white border border-indigo-300 text-[13px] outline-none" />
                            </div>
                            <div v-for="f in folders.folders" :key="'f' + f.id" class="navrow group/f" :class="{ on: active.type === 'folder' && active.id === f.id }" @click="pickFolder('folder', f.id, f.name)">
                                <Folder class="w-4 h-4 text-slate-400 shrink-0" /> <span class="truncate">{{ f.name }}</span>
                                <span class="ml-auto text-[11px] text-slate-500 group-hover/f:hidden">{{ f.count }}</span>
                                <button class="ml-auto hidden group-hover/f:grid w-6 h-6 rounded-md place-items-center text-slate-400 hover:text-red-600 hover:bg-white" title="Delete folder" @click.stop="askDeleteFolder(f)"><Trash2 class="w-3.5 h-3.5" /></button>
                            </div>
                            <p v-if="!creatingFolder && !folders.folders.length" class="px-2.5 text-[11px] text-slate-500 leading-snug">No folders yet. Click + to create one.</p>
                        </div>
                        <div class="p-3 border-t border-slate-200">
                            <div class="flex items-start gap-2 text-[11px] text-slate-400 leading-snug"><ShieldCheck class="w-3.5 h-3.5 mt-0.5 shrink-0 text-emerald-500" /> Showing {{ folders.projects_visible }} of {{ folders.projects_total }} projects — only those you're a member of.</div>
                        </div>
                    </aside>

                    <!-- Main -->
                    <main class="flex-1 flex flex-col min-w-0">
                        <!-- LIBRARY -->
                        <template v-if="view === 'library'">
                            <div class="is-bar">
                                <span class="text-[14px] font-semibold text-slate-900">{{ active.label }}</span>
                                <div class="relative ml-2">
                                    <Search class="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                                    <input v-model="libQuery" @keyup.enter="loadAssets" placeholder="Search this library" class="w-64 h-9 pl-9 pr-3 rounded-lg bg-slate-100 border border-transparent focus:bg-white focus:border-indigo-300 text-[13px] outline-none" />
                                </div>
                                <div class="flex-1"></div>
                                <label class="h-9 px-4 rounded-lg text-[13px] font-semibold text-white bg-indigo-600 hover:bg-indigo-700 flex items-center gap-1.5 cursor-pointer"><Upload class="w-4 h-4" /> Upload<input type="file" accept="image/*" class="hidden" data-testid="is-lib-upload" @change="onLibUpload" /></label>
                            </div>
                            <div class="flex-1 overflow-y-auto p-5">
                                <p v-if="error" class="is-error mb-3">{{ error }}</p>
                                <div class="grid gap-4" style="grid-template-columns:repeat(auto-fill,minmax(180px,1fr))">
                                    <label class="aspect-[4/3] rounded-xl border-2 border-dashed border-slate-200 hover:border-indigo-400 hover:bg-indigo-50/40 flex flex-col items-center justify-center gap-1 cursor-pointer text-slate-500">
                                        <UploadCloud class="w-6 h-6" /><span class="text-[12px] font-semibold text-slate-500">Drop or upload</span>
                                        <input type="file" accept="image/*" class="hidden" @change="onLibUpload" />
                                    </label>
                                    <div v-for="a in assets" :key="a.id" class="media-card group cursor-pointer relative" @click="openLibraryAsset(a)">
                                        <div class="aspect-[4/3] rounded-xl overflow-hidden bg-slate-100 ring-1 ring-slate-200 group-hover:ring-indigo-400">
                                            <img class="card-img w-full h-full object-cover" :src="a.url" loading="lazy" />
                                        </div>
                                        <div class="absolute top-2 right-2 z-10 flex gap-1.5" :class="moveMenuId === a.id ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'">
                                            <span class="w-7 h-7 rounded-lg bg-white/90 grid place-items-center text-slate-600" title="Edit"><Pencil class="w-3.5 h-3.5" /></span>
                                            <button class="w-7 h-7 rounded-lg bg-white/90 grid place-items-center hover:text-indigo-600" :class="moveMenuId === a.id ? 'text-indigo-600' : 'text-slate-600'" title="Move" @click.stop="toggleMoveMenu(a)"><FolderInput class="w-3.5 h-3.5" /></button>
                                            <button class="w-7 h-7 rounded-lg bg-white/90 grid place-items-center text-slate-600 hover:text-red-600" title="Delete" @click.stop="deleteAsset(a)"><Trash2 class="w-3.5 h-3.5" /></button>
                                        </div>
                                        <template v-if="moveMenuId === a.id">
                                            <div class="fixed inset-0 z-30" @click.stop="closeMoveMenu"></div>
                                            <div class="absolute top-11 right-2 z-40 w-52 bg-white rounded-xl shadow-xl ring-1 ring-slate-200 py-1.5" @click.stop>
                                                <div class="px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-slate-500">Move to</div>
                                                <div class="max-h-60 overflow-y-auto">
                                                    <button class="menurow" @click.stop="moveAsset(a, 'all')"><LayoutGrid class="w-4 h-4 text-slate-400" /> All media</button>
                                                    <template v-if="folders.projects.length"><div class="menusep">Projects</div>
                                                        <button v-for="p in folders.projects" :key="'mp' + p.id" class="menurow" @click.stop="moveAsset(a, 'project', p.id)"><Folder class="w-4 h-4 text-amber-500" /> <span class="truncate">{{ p.name }}</span></button>
                                                    </template>
                                                    <template v-if="folders.folders.length"><div class="menusep">Folders</div>
                                                        <button v-for="f in folders.folders" :key="'mf' + f.id" class="menurow" @click.stop="moveAsset(a, 'folder', f.id)"><Folder class="w-4 h-4 text-slate-400" /> <span class="truncate">{{ f.name }}</span></button>
                                                    </template>
                                                </div>
                                            </div>
                                        </template>
                                        <div class="px-1 pt-1.5"><div class="text-[13px] font-medium text-slate-700 truncate">{{ a.filename }}</div><div class="text-[11px] text-slate-500">{{ a.width }}×{{ a.height }}<span v-if="a.size_bytes"> · {{ Math.round(a.size_bytes / 1024) }} KB</span></div></div>
                                    </div>
                                </div>
                                <p v-if="!loading && !assets.length" class="text-center text-slate-500 text-[13px] py-10">No media here yet — upload or search online.</p>
                            </div>
                        </template>

                        <!-- ONLINE -->
                        <template v-else-if="view === 'online'">
                            <div class="is-bar">
                                <SelectMenu v-model="source" :options="[{ value: 'pexels', label: 'Pexels' }, { value: 'openverse', label: 'Openverse (CC)' }]" />
                                <div class="relative flex-1 max-w-xl">
                                    <Search class="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                                    <input v-model="onlineQuery" @keyup.enter="runSearch()" data-testid="is-query" placeholder="Search royalty-free images" class="w-full h-9 pl-9 pr-3 rounded-lg bg-slate-100 border border-transparent focus:bg-white focus:border-indigo-300 text-[13px] outline-none" />
                                </div>
                                <button class="h-9 px-4 rounded-lg text-[13px] font-semibold text-white bg-indigo-600 hover:bg-indigo-700" data-testid="is-search-btn" @click="runSearch()">Search</button>
                            </div>
                            <div class="flex-1 overflow-y-auto p-5">
                                <p v-if="error" class="is-error mb-3" data-testid="is-error">{{ error }}</p>
                                <div class="grid gap-4" data-testid="is-results" style="grid-template-columns:repeat(auto-fill,minmax(190px,1fr))">
                                    <button v-for="r in results" :key="r.id" class="media-card is-card group cursor-pointer" :disabled="busy" @click="pickStock(r)">
                                        <div class="aspect-[4/3] rounded-xl overflow-hidden bg-slate-100 ring-1 ring-slate-200 group-hover:ring-indigo-400 relative">
                                            <img class="card-img w-full h-full object-cover" :src="r.thumb" loading="lazy" />
                                            <span v-if="r.credit" class="absolute left-0 right-0 bottom-0 text-[10px] text-white px-2 py-2.5 bg-gradient-to-t from-black/70 truncate">{{ r.credit }}</span>
                                        </div>
                                    </button>
                                </div>
                                <p v-if="!loading && !results.length && !error" class="text-center text-slate-500 text-[13px] py-10">Search Openverse or Pexels for royalty-free imagery.</p>
                            </div>
                        </template>

                        <!-- EDIT -->
                        <template v-else>
                            <div class="flex-1 flex min-h-0">
                                <div class="flex-1 flex flex-col min-w-0">
                                    <div class="is-editbar">
                                        <button class="h-8 px-2.5 rounded-lg text-[12px] font-semibold text-slate-500 hover:bg-slate-100 flex items-center gap-1" data-testid="is-back" @click="view = 'library'"><ChevronLeft class="w-4 h-4" /> Back</button>
                                        <span class="text-[13px] font-medium text-slate-600 truncate">{{ editAlt || 'Untitled' }}</span>
                                    </div>
                                    <div class="flex-1 is-chk grid place-items-center p-6 overflow-hidden">
                                        <div class="relative" :style="{ width: dispW + 'px', height: dispH + 'px' }">
                                            <img ref="imgRef" :src="editSrc" crossorigin="anonymous" @load="onImgLoad" :style="filterStyle" alt="" draggable="false" />
                                            <div class="is-cropbox" data-testid="is-cropbox" :style="{ left: crop.x + 'px', top: crop.y + 'px', width: crop.w + 'px', height: crop.h + 'px' }" @pointerdown="pd($event, 'move')">
                                                <span class="is-h nw" @pointerdown.stop="pd($event, 'nw')"></span><span class="is-h ne" @pointerdown.stop="pd($event, 'ne')"></span><span class="is-h sw" @pointerdown.stop="pd($event, 'sw')"></span><span class="is-h se" @pointerdown.stop="pd($event, 'se')"></span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <aside class="w-[300px] border-l border-slate-200 flex flex-col min-h-0">
                                    <div class="flex-1 overflow-y-auto p-4 space-y-5">
                                        <div><div class="cg-h">Crop</div><div class="seg"><button v-for="a in ASPECTS" :key="a[0]" :class="{ on: String(aspect) === String(a[0]) }" @click="setAspect(a[0])">{{ a[1] }}</button></div></div>
                                        <div><div class="cg-h">Transform</div><div class="grid grid-cols-4 gap-2">
                                            <button class="tbtn" title="Rotate left" @click="rotate(-90)"><RotateCcw class="w-4 h-4" /></button>
                                            <button class="tbtn" title="Rotate right" @click="rotate(90)"><RotateCw class="w-4 h-4" /></button>
                                            <button class="tbtn" :class="{ on: flipH }" title="Flip horizontal" @click="flipH = !flipH; touch()"><FlipHorizontal2 class="w-4 h-4" /></button>
                                            <button class="tbtn" :class="{ on: flipV }" title="Flip vertical" @click="flipV = !flipV; touch()"><FlipVertical2 class="w-4 h-4" /></button>
                                        </div></div>
                                        <div><div class="cg-h">Adjust</div>
                                            <div class="flex items-center justify-between mb-2.5"><span class="text-[12px] text-slate-600">Black &amp; white</span><button data-testid="is-bw" class="tog" :class="{ on: adj.bw }" @click="adj.bw = !adj.bw; touch()"><span class="knob"></span></button></div>
                                            <div v-for="s in [['br','Brightness',50,150,'%'],['co','Contrast',50,150,'%'],['sa','Saturation',0,200,'%'],['se','Sepia',0,100,'%'],['bl','Blur',0,12,'px']]" :key="s[0]">
                                                <label class="text-[12px] text-slate-600 mb-1 flex justify-between">{{ s[1] }} <span class="text-slate-500">{{ adj[s[0]] }}{{ s[4] }}</span></label>
                                                <input type="range" :min="s[2]" :max="s[3]" v-model.number="adj[s[0]]" @input="touch" class="w-full mb-3" />
                                            </div>
                                        </div>
                                        <div><div class="cg-h">Size</div>
                                            <div class="flex items-end gap-2">
                                                <label class="flex-1 block"><span class="text-[10px] text-slate-500">Width</span><div class="numbox"><input type="number" v-model.number="size.ow" @input="onW" data-testid="is-w" /><span>px</span></div></label>
                                                <button class="mb-0.5 w-8 h-8 rounded-lg border grid place-items-center shrink-0" :class="sizeLock ? 'border-indigo-300 bg-indigo-50 text-indigo-600' : 'border-slate-200 bg-white text-slate-400'" title="Lock aspect ratio" @click="sizeLock = !sizeLock"><Link2 class="w-4 h-4" /></button>
                                                <label class="flex-1 block"><span class="text-[10px] text-slate-500">Height</span><div class="numbox"><input type="number" v-model.number="size.oh" @input="onH" data-testid="is-h" /><span>px</span></div></label>
                                            </div>
                                            <SelectMenu model-value="" class="mt-2 w-full" :options="PRESETS.map(p => ({ value: p[0], label: p[1] }))" @update:model-value="applyPreset" />
                                        </div>
                                        <div><div class="cg-h">Export</div>
                                            <div class="flex items-center gap-2 mb-2"><span class="w-16 text-[12px] text-slate-600">Format</span><SelectMenu v-model="format" :options="[{ value: 'image/webp', label: 'WebP (smaller)' }, { value: 'image/jpeg', label: 'JPEG' }, { value: 'image/png', label: 'PNG' }]" /></div>
                                            <div class="flex items-center gap-2"><span class="w-16 text-[12px] text-slate-600">Quality</span><input type="range" min="0.3" max="1" step="0.02" v-model.number="quality" class="flex-1" /><span class="text-[11px] text-slate-500 w-8 text-right">{{ Math.round(quality * 100) }}%</span></div>
                                            <div class="mt-3 flex items-center gap-2 text-[12px] text-slate-500 bg-slate-50 border border-slate-100 rounded-lg px-3 py-2" data-testid="is-est">Output <strong class="text-slate-800 font-mono">{{ size.ow }}×{{ size.oh }}</strong> · ≈ <strong class="text-slate-800 font-mono">{{ estKB != null ? estKB + ' KB' : '—' }}</strong></div>
                                            <p v-if="error" class="is-error mt-2">{{ error }}</p>
                                        </div>
                                    </div>
                                    <div class="p-3 border-t border-slate-200 flex gap-2 items-center">
                                        <button class="h-9 px-3 rounded-lg text-[12px] font-semibold text-slate-600 border border-slate-200 hover:bg-slate-50" :disabled="busy" @click="saveToLibrary">Save to library</button>
                                        <div class="flex-1"></div>
                                        <button class="h-9 px-4 rounded-lg text-[12px] font-semibold text-white bg-indigo-600 hover:bg-indigo-700 flex items-center gap-1.5 disabled:opacity-60" data-testid="is-use" :disabled="busy" @click="useImage"><Check class="w-4 h-4" /> {{ busy ? 'Working…' : 'Use image' }}</button>
                                    </div>
                                </aside>
                            </div>
                        </template>
                    </main>
                </div>

                <!-- Delete-folder confirmation -->
                <div v-if="folderToDelete" class="absolute inset-0 z-50 grid place-items-center bg-slate-900/40 rounded-2xl" @click.self="cancelDeleteFolder">
                    <div class="w-[380px] bg-white rounded-2xl shadow-2xl p-5">
                        <div class="flex items-center gap-2.5 mb-2"><div class="w-9 h-9 rounded-lg bg-red-50 text-red-700 grid place-items-center"><Trash2 class="w-4.5 h-4.5" /></div><div class="text-[15px] font-semibold text-slate-900">Delete folder</div></div>
                        <p class="text-[13px] text-slate-600 leading-relaxed">
                            <template v-if="folderToDelete.count">Delete <strong>{{ folderToDelete.name }}</strong>? It contains {{ folderToDelete.count }} image{{ folderToDelete.count === 1 ? '' : 's' }} — they will be moved to <strong>All media</strong>, not deleted.</template>
                            <template v-else>Delete the folder <strong>{{ folderToDelete.name }}</strong>?</template>
                        </p>
                        <div class="flex justify-end gap-2 mt-5">
                            <button class="h-9 px-3.5 rounded-lg text-[13px] font-semibold text-slate-600 border border-slate-200 hover:bg-slate-50" @click="cancelDeleteFolder">Cancel</button>
                            <button class="h-9 px-3.5 rounded-lg text-[13px] font-semibold text-white bg-red-600 hover:bg-red-700" @click="confirmDeleteFolder">Delete folder</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </teleport>
</template>

<style scoped>
.is-overlay { position: fixed; inset: 0; z-index: 70; background: rgba(15,23,42,.55); backdrop-filter: blur(2px); display: flex; align-items: center; justify-content: center; padding: 22px; }
.is-modal { position: relative; width: min(1160px, 97vw); height: min(760px, 94vh); background: #fff; border-radius: 16px; box-shadow: 0 24px 70px rgba(2,6,23,.45); display: flex; flex-direction: column; overflow: hidden; }
.is-head { height: 56px; display: flex; align-items: center; gap: 12px; padding: 0 16px; border-bottom: 1px solid #eef1f6; flex: none; }
.tabbtn { height: 36px; padding: 0 12px; border: 0; background: transparent; border-radius: 8px; font-size: 13px; font-weight: 600; color: #64748b; cursor: pointer; }
.tabbtn:hover { background: #f1f5f9; }
.seg-on { background: #eef2ff !important; color: #4338ca !important; }
.is-x { width: 36px; height: 36px; border: 0; background: transparent; border-radius: 8px; display: grid; place-items: center; color: #94a3b8; cursor: pointer; }
.is-x:hover { background: #f1f5f9; }
.nav-h { padding: 0 8px 4px; font-size: 10px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: #94a3b8; }
.navrow { width: 100%; display: flex; align-items: center; gap: 10px; padding: 0 10px; height: 36px; border: 0; background: transparent; border-radius: 8px; font-size: 13px; font-weight: 500; color: #475569; cursor: pointer; text-align: left; }
.navrow:hover { background: #f1f5f9; }
.navrow.on { background: #eef2ff; color: #4338ca; }
.is-bar { height: 56px; display: flex; align-items: center; gap: 12px; padding: 0 20px; border-bottom: 1px solid #f1f5f9; flex: none; }
.is-editbar { height: 48px; display: flex; align-items: center; gap: 8px; padding: 0 16px; border-bottom: 1px solid #f1f5f9; flex: none; }
.is-error { color: #dc2626; font-size: 12px; }
.media-card.is-card { border: 0; padding: 0; background: transparent; }
.menurow { width: 100%; display: flex; align-items: center; gap: 10px; padding: 0 12px; height: 32px; border: 0; background: transparent; font-size: 13px; color: #475569; cursor: pointer; text-align: left; }
.menurow:hover { background: #f8fafc; color: #0f172a; }
.menusep { padding: 8px 12px 2px; font-size: 10px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: #94a3b8; }
.card-img { transition: transform .18s ease; }
.media-card:hover .card-img { transform: scale(1.05); }
.is-chk { background-image: linear-gradient(45deg,#1e293b 25%,transparent 25%),linear-gradient(-45deg,#1e293b 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#1e293b 75%),linear-gradient(-45deg,transparent 75%,#1e293b 75%); background-size: 22px 22px; background-position: 0 0,0 11px,11px -11px,-11px 0; background-color: #0f172a; }
.is-chk img { display: block; }
.is-cropbox { position: absolute; box-shadow: 0 0 0 9999px rgba(15,23,42,.55); border: 1.5px solid #fff; box-sizing: border-box; cursor: move; }
.is-h { position: absolute; width: 12px; height: 12px; background: #fff; border: 1.5px solid #4338ca; border-radius: 2px; }
.is-h.nw { left: -6px; top: -6px; cursor: nwse-resize; } .is-h.ne { right: -6px; top: -6px; cursor: nesw-resize; }
.is-h.sw { left: -6px; bottom: -6px; cursor: nesw-resize; } .is-h.se { right: -6px; bottom: -6px; cursor: nwse-resize; }
.cg-h { font-size: 10px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: #94a3b8; margin-bottom: 8px; }
.seg { display: flex; background: #eef1f6; border-radius: 8px; padding: 2px; gap: 2px; }
.seg button { flex: 1; border: 0; background: transparent; font-size: 11px; font-weight: 600; color: #64748b; padding: 5px 0; border-radius: 5px; cursor: pointer; }
.seg button.on { background: #fff; color: #0f172a; box-shadow: 0 1px 2px rgba(15,23,42,.12); }
.tbtn { height: 36px; border: 1px solid #e2e8f0; border-radius: 8px; display: grid; place-items: center; color: #64748b; background: #fff; cursor: pointer; }
.tbtn:hover { background: #f8fafc; } .tbtn.on { background: #eef2ff; border-color: #6366f1; color: #4338ca; }
.tog { width: 40px; height: 22px; border-radius: 999px; background: #cbd5e1; border: 0; position: relative; cursor: pointer; }
.tog.on { background: #4338ca; } .tog .knob { position: absolute; top: 2px; left: 2px; width: 18px; height: 18px; border-radius: 50%; background: #fff; transition: left .15s; } .tog.on .knob { left: 20px; }
.numbox { display: flex; align-items: center; height: 32px; border: 1px solid #e2e8f0; border-radius: 8px; background: #f8fafc; padding: 0 8px; margin-top: 2px; }
.numbox input { width: 100%; background: transparent; border: 0; outline: none; font-size: 12px; min-width: 0; }
.numbox span { font-size: 10px; color: #94a3b8; }
input[type=range] { accent-color: #4f46e5; }
</style>
